/**
 * Phase F — NTN Case Queue (/staff-ops/cases).
 * The main staff working queue over overall applicants/orders/cases.
 * Persisted state: Active | Completed. Active sub-buckets (New / Needs
 * Review / In Progress / Waiting) are computed by the backend.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { apiClient } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, CheckCircle2, RotateCcw, AlertTriangle, Sparkles, Pencil, Hash } from "lucide-react";

const BUCKET_META = {
  new: { label: "New", tone: "bg-rose-600 text-white" },
  needs_review: { label: "Needs Review", tone: "bg-purple-100 text-purple-800" },
  in_progress: { label: "In Progress", tone: "bg-indigo-100 text-indigo-800" },
  waiting: { label: "Waiting", tone: "bg-amber-100 text-amber-800" },
};
const TRACK_STATUS_TONE = {
  not_started: "bg-slate-100 text-slate-600",
  in_progress: "bg-indigo-100 text-indigo-800",
  waiting_on_third_party: "bg-amber-100 text-amber-800",
  needs_review: "bg-purple-100 text-purple-800",
  completed: "bg-emerald-100 text-emerald-800",
  unable_to_verify: "bg-slate-200 text-slate-700",
  cancelled: "bg-slate-200 text-slate-500",
};

export function timeAgo(iso) {
  if (!iso) return "—";
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

function TrackChip({ t }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${t.is_new ? "bg-rose-600 text-white" : TRACK_STATUS_TONE[t.canonical_status] || "bg-slate-100 text-slate-600"}`}
      data-testid={`case-track-chip-${t.track}`}
    >
      {t.label} · {t.is_new ? "NEW" : t.canonical_label}
    </span>
  );
}

function CaseRow({ r, view, onOpen, onComplete, onReopen, onEditInquiry }) {
  const meta = BUCKET_META[r.bucket] || {};
  const orderingLabel = r.ordering_company?.label || r.company || "—";
  return (
    <div
      className="p-4 hover:bg-slate-50 cursor-pointer flex flex-wrap items-center gap-4"
      onClick={onOpen}
      data-testid={`case-row-${r.screening_id}`}
    >
      <div className="flex-1 min-w-[240px]">
        <div className="flex items-center gap-2">
          {r.is_new && view === "active" && (
            <Badge className="bg-rose-600 text-white animate-pulse font-bold tracking-wide" data-testid={`case-new-badge-${r.screening_id}`}>
              NEW
            </Badge>
          )}
          <span className="font-semibold text-slate-900">{r.applicant_name || "—"}</span>
        </div>
        <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5 text-xs text-slate-500">
          <div className="truncate" data-testid={`case-ordering-account-${r.screening_id}`}>
            <span className="text-[10px] uppercase tracking-[0.14em] text-slate-400 mr-1">Ordering Account</span>
            <span className="text-slate-700">{orderingLabel}</span>
          </div>
          <div className="truncate flex items-center gap-1" data-testid={`case-inquiry-${r.screening_id}`}>
            <span className="text-[10px] uppercase tracking-[0.14em] text-slate-400 mr-1">Inquiry #</span>
            {r.inquiry_number ? (
              <span className="font-mono text-slate-700">{r.inquiry_number}</span>
            ) : (
              <span className="italic text-slate-400">Not Assigned</span>
            )}
            <button
              type="button"
              className="ml-1 inline-flex items-center text-slate-400 hover:text-slate-700"
              onClick={(e) => { e.stopPropagation(); onEditInquiry(r); }}
              data-testid={`case-inquiry-edit-btn-${r.screening_id}`}
              aria-label="Edit inquiry number"
            >
              <Pencil className="h-3 w-3" />
            </button>
          </div>
          <div className="truncate sm:col-span-2 text-slate-500">{r.property_address}</div>
        </div>
        <div className="text-[11px] text-slate-400 mt-1" data-testid={`case-received-${r.screening_id}`}>
          Received {timeAgo(r.created_at)}
          {view === "completed" && r.completed_at && (
            <> · Completed {timeAgo(r.completed_at)} by {r.completed_by_name}</>
          )}
        </div>
      </div>
      {view === "active" && r.bucket && !r.is_new && (
        <Badge className={meta.tone} data-testid={`case-bucket-badge-${r.screening_id}`}>{meta.label}</Badge>
      )}
      <div className="flex flex-wrap gap-1.5 max-w-[360px]">
        {(r.tracks || []).map((t) => <TrackChip key={t.track} t={t} />)}
      </div>
      {r.open_risk_indicator_count > 0 && (
        <div className="flex items-center gap-1 text-xs text-amber-700 font-medium">
          <AlertTriangle className="h-3.5 w-3.5" /> {r.open_risk_indicator_count}
        </div>
      )}
      {view === "active" ? (
        <Button
          size="sm" variant="outline"
          className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
          onClick={(e) => { e.stopPropagation(); onComplete(r); }}
          data-testid={`case-complete-btn-${r.screening_id}`}
        >
          <CheckCircle2 className="h-4 w-4 mr-1.5" /> Mark Complete
        </Button>
      ) : (
        <Button
          size="sm" variant="outline"
          onClick={(e) => { e.stopPropagation(); onReopen(r); }}
          data-testid={`case-reopen-btn-${r.screening_id}`}
        >
          <RotateCcw className="h-4 w-4 mr-1.5" /> Reopen
        </Button>
      )}
    </div>
  );
}

export default function OpsCaseQueuePage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [view, setView] = useState("active");
  const [bucket, setBucket] = useState("all");
  const [q, setQ] = useState("");
  const [subscriber, setSubscriber] = useState("");
  const [loading, setLoading] = useState(true);
  const [completeTarget, setCompleteTarget] = useState(null);
  const [completeNote, setCompleteNote] = useState("");
  const [reopenTarget, setReopenTarget] = useState(null);
  const [reopenReason, setReopenReason] = useState("");
  const [inquiryTarget, setInquiryTarget] = useState(null);
  const [inquiryValue, setInquiryValue] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiClient.get("/staff-ops/cases", { params: { view, bucket, q, subscriber, limit: 200 } })
      .then(({ data }) => { setRows(data.rows || []); setCounts(data.counts || {}); })
      .catch(() => { setRows([]); setCounts({}); })
      .finally(() => setLoading(false));
  }, [view, bucket, q, subscriber]);

  useEffect(() => { load(); }, [load]);

  const doComplete = async () => {
    if (!completeTarget) return;
    setBusy(true);
    try {
      await apiClient.post(`/staff-ops/cases/${completeTarget.screening_id}/complete`, { note: completeNote || null });
      toast.success(`Case completed — ${completeTarget.applicant_name}`);
      setCompleteTarget(null); setCompleteNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete case");
    } finally { setBusy(false); }
  };

  const doReopen = async () => {
    if (!reopenTarget || reopenReason.trim().length < 3) return;
    setBusy(true);
    try {
      await apiClient.post(`/staff-ops/cases/${reopenTarget.screening_id}/reopen`, { reason: reopenReason.trim() });
      toast.success(`Case reopened — ${reopenTarget.applicant_name}`);
      setReopenTarget(null); setReopenReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reopen case");
    } finally { setBusy(false); }
  };

  const openInquiryEditor = (row) => {
    setInquiryTarget(row);
    setInquiryValue(row.inquiry_number || "");
  };
  const saveInquiry = async () => {
    if (!inquiryTarget) return;
    const val = (inquiryValue || "").trim();
    if (val && !/^\d{1,32}$/.test(val)) {
      toast.error("Inquiry # must be digits only (1–32 characters).");
      return;
    }
    setBusy(true);
    try {
      await apiClient.patch(
        `/staff-ops/case/${inquiryTarget.screening_id}/inquiry-number`,
        { value: val || null },
      );
      toast.success(val ? `Inquiry # set to ${val}` : "Inquiry # cleared");
      setInquiryTarget(null); setInquiryValue("");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail
          : Array.isArray(detail) ? detail[0]?.msg
          : "Could not save Inquiry #"
      );
    } finally { setBusy(false); }
  };

  const bucketTabs = [
    { key: "all", label: `All Active (${counts.active_total ?? 0})` },
    { key: "new", label: `New (${counts.new ?? 0})`, hot: (counts.new ?? 0) > 0 },
    { key: "needs_review", label: `Needs Review (${counts.needs_review ?? 0})` },
    { key: "in_progress", label: `In Progress (${counts.in_progress ?? 0})` },
    { key: "waiting", label: `Waiting (${counts.waiting ?? 0})` },
  ];

  return (
    <AppShell>
      <div className="space-y-6" data-testid="ops-case-queue">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-overline text-slate-500 mb-2">Operations · Case Queue</div>
            <h1 className="ntn-h2">NTN Case Queue</h1>
            <p className="ntn-body text-slate-500 mt-1">
              Every applicant/order NTN still needs to work. Overall case state is independent of individual verification tracks.
            </p>
          </div>
          <Link to="/staff-ops" className="text-sm text-slate-500 hover:text-slate-800" data-testid="case-queue-back">← Operations dashboard</Link>
        </div>

        {/* View toggle */}
        <div className="flex gap-2">
          <Button
            variant={view === "active" ? "default" : "outline"}
            onClick={() => { setView("active"); setBucket("all"); }}
            data-testid="case-view-active"
          >
            Active ({counts.active_total ?? 0})
          </Button>
          <Button
            variant={view === "completed" ? "default" : "outline"}
            onClick={() => setView("completed")}
            data-testid="case-view-completed"
          >
            Completed ({counts.completed ?? 0})
          </Button>
        </div>

        <Card className="p-4 bg-white border-slate-200 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search applicant, company or property…"
                     className="pl-9" data-testid="case-queue-search" />
            </div>
            <div className="relative sm:w-72">
              <Input value={subscriber} onChange={(e) => setSubscriber(e.target.value)}
                     placeholder="Subscriber: All Subscribers"
                     className="pl-3" data-testid="case-queue-subscriber-filter" />
            </div>
          </div>
          {view === "active" && (
            <div className="flex flex-wrap gap-2">
              {bucketTabs.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setBucket(t.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                    bucket === t.key
                      ? "bg-slate-900 text-white"
                      : t.hot
                        ? "bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                  data-testid={`case-bucket-tab-${t.key}`}
                >
                  {t.hot && <Sparkles className="h-3 w-3 inline mr-1 -mt-0.5" />}
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card className="bg-white border-slate-200 divide-y">
          {loading && <div className="p-6 text-slate-500 text-sm">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="p-8 text-center text-slate-500 text-sm" data-testid="case-queue-empty">
              {view === "active" ? "No active cases match." : "No completed cases yet."}
            </div>
          )}
          {!loading && rows.map((r) => (
            <CaseRow
              key={r.screening_id}
              r={r}
              view={view}
              onOpen={() => navigate(`/screenings/${r.screening_id}`)}
              onComplete={setCompleteTarget}
              onReopen={setReopenTarget}
              onEditInquiry={openInquiryEditor}
            />
          ))}
        </Card>

        {/* Mark Complete dialog */}
        <Dialog open={!!completeTarget} onOpenChange={(o) => { if (!o) { setCompleteTarget(null); setCompleteNote(""); } }}>
          <DialogContent data-testid="case-complete-dialog">
            <DialogHeader>
              <DialogTitle>Mark case complete</DialogTitle>
              <DialogDescription>
                {completeTarget?.applicant_name} — this removes the case from the active NTN work queue.
                Nothing PM-facing changes: results, DecisionPoint, risk banners, notes and documents are all preserved.
              </DialogDescription>
            </DialogHeader>
            <Textarea
              value={completeNote}
              onChange={(e) => setCompleteNote(e.target.value)}
              placeholder="Optional completion note…"
              maxLength={500}
              data-testid="case-complete-note"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => { setCompleteTarget(null); setCompleteNote(""); }} data-testid="case-complete-cancel">Cancel</Button>
              <Button onClick={doComplete} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="case-complete-confirm">
                <CheckCircle2 className="h-4 w-4 mr-1.5" /> Mark Complete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reopen dialog */}
        <Dialog open={!!reopenTarget} onOpenChange={(o) => { if (!o) { setReopenTarget(null); setReopenReason(""); } }}>
          <DialogContent data-testid="case-reopen-dialog">
            <DialogHeader>
              <DialogTitle>Reopen case</DialogTitle>
              <DialogDescription>
                {reopenTarget?.applicant_name} — returns the case to the active work queue.
                Previous completion history is preserved. A reason is required.
              </DialogDescription>
            </DialogHeader>
            <Textarea
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
              placeholder="Reason for reopening (required)…"
              maxLength={500}
              data-testid="case-reopen-reason"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => { setReopenTarget(null); setReopenReason(""); }} data-testid="case-reopen-cancel">Cancel</Button>
              <Button onClick={doReopen} disabled={busy || reopenReason.trim().length < 3} data-testid="case-reopen-confirm">
                <RotateCcw className="h-4 w-4 mr-1.5" /> Reopen Case
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Inquiry # quick-edit dialog */}
        <Dialog open={!!inquiryTarget} onOpenChange={(o) => { if (!o) { setInquiryTarget(null); setInquiryValue(""); } }}>
          <DialogContent data-testid="case-inquiry-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Hash className="h-4 w-4" /> NTN Inquiry #</DialogTitle>
              <DialogDescription>
                {inquiryTarget?.applicant_name} · assign or edit the NTN-issued Inquiry #.
                Digits only, 1–32 characters. Leave blank to clear.
              </DialogDescription>
            </DialogHeader>
            <Input
              value={inquiryValue}
              onChange={(e) => setInquiryValue(e.target.value.replace(/\D/g, ""))}
              placeholder="e.g. 00123456"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={32}
              autoFocus
              data-testid="case-inquiry-input"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => { setInquiryTarget(null); setInquiryValue(""); }} data-testid="case-inquiry-cancel">Cancel</Button>
              <Button onClick={saveInquiry} disabled={busy} data-testid="case-inquiry-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}
